import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealCard from "../../components/DealCard";
import DealDetailModal from "../../components/DealDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import {
  TrendingUp, Building2, Search, SlidersHorizontal, X,
  Loader2, LayoutGrid, List, ChevronLeft, ChevronRight
} from "lucide-react";

const DEALS_PER_PAGE = 12;

const DEAL_TYPES = [
  { value: "all", label: "All Types" },
  { value: "flip", label: "Flip" },
  { value: "rental", label: "Rental" },
  { value: "development", label: "Development" },
];

const PROPERTY_TYPES = [
  { value: "all", label: "All Properties" },
  { value: "single_family", label: "Single Family" },
  { value: "multi_family", label: "Multi-Family" },
  { value: "apartment", label: "Apartment" },
  { value: "commercial", label: "Commercial" },
  { value: "land", label: "Land" },
  { value: "mixed_use", label: "Mixed Use" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "highest_profit", label: "Highest Profit" },
  { value: "lowest_price", label: "Lowest Price" },
  { value: "highest_arv", label: "Highest ARV" },
];

const US_STATES = [
  "All States", "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const InvestorLiveDealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [dealTypeFilter, setDealTypeFilter] = useState("all");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("All States");
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [arvRange, setArvRange] = useState([0, 20000000]);
  const [profitRange, setProfitRange] = useState([-1000000, 5000000]);
  const [sortBy, setSortBy] = useState("newest");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setDeals(response.data.deals || response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeals(); }, []);

  const filteredDeals = useMemo(() => {
    let result = deals.filter(d =>
      d.status === "active" || d.status === "approved" ||
      d.status === "reserved" || d.status === "in_closing"
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.property_address?.toLowerCase().includes(query) ||
        d.city?.toLowerCase().includes(query) ||
        d.state?.toLowerCase().includes(query)
      );
    }

    if (dealTypeFilter !== "all") result = result.filter(d => d.deal_type === dealTypeFilter);
    if (propertyTypeFilter !== "all") result = result.filter(d => d.property_type === propertyTypeFilter);
    if (stateFilter !== "All States") result = result.filter(d => d.state === stateFilter);

    result = result.filter(d =>
      (d.asking_price ?? 0) >= priceRange[0] && (d.asking_price ?? 0) <= priceRange[1]
    );
    result = result.filter(d =>
      (d.estimated_arv ?? 0) >= arvRange[0] && (d.estimated_arv ?? 0) <= arvRange[1]
    );
    result = result.filter(d => {
      const profit = (d.estimated_arv ?? 0) - (d.asking_price ?? 0) - (d.estimated_rehab_cost ?? 0);
      return profit >= profitRange[0] && profit <= profitRange[1];
    });

    switch (sortBy) {
      case "highest_profit":
        result.sort((a, b) => (b.estimated_arv - b.asking_price - b.estimated_rehab_cost) - (a.estimated_arv - a.asking_price - a.estimated_rehab_cost));
        break;
      case "lowest_price":
        result.sort((a, b) => a.asking_price - b.asking_price);
        break;
      case "highest_arv":
        result.sort((a, b) => b.estimated_arv - a.estimated_arv);
        break;
      default:
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return result;
  }, [deals, searchQuery, dealTypeFilter, propertyTypeFilter, stateFilter, priceRange, arvRange, profitRange, sortBy]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, dealTypeFilter, propertyTypeFilter, stateFilter, priceRange, arvRange, profitRange, sortBy]);

  const totalPages = Math.ceil(filteredDeals.length / DEALS_PER_PAGE);
  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * DEALS_PER_PAGE;
    return filteredDeals.slice(start, start + DEALS_PER_PAGE);
  }, [filteredDeals, currentPage]);

  const clearFilters = () => {
    setSearchQuery("");
    setDealTypeFilter("all");
    setPropertyTypeFilter("all");
    setStateFilter("All States");
    setPriceRange([0, 10000000]);
    setArvRange([0, 20000000]);
    setProfitRange([-1000000, 5000000]);
    setSortBy("newest");
  };

  const hasActiveFilters =
    searchQuery || dealTypeFilter !== "all" || propertyTypeFilter !== "all" ||
    stateFilter !== "All States" || priceRange[0] > 0 || priceRange[1] < 10000000 ||
    arvRange[0] > 0 || arvRange[1] < 20000000 || profitRange[0] > -1000000 || profitRange[1] < 5000000;

  const formatCurrency = (value) => "$" + value.toLocaleString();

  return (
    <DashboardLayout title="Live Deals">
      <div className="space-y-6" data-testid="investor-live-deals">
        {/* Filters */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5" />
                Live Deals
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </Button>
                <div className="flex items-center border rounded-lg">
                  <Button variant={viewMode === "grid" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("grid")} className="rounded-r-none" data-testid="view-grid-btn">
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button variant={viewMode === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("list")} className="rounded-l-none" data-testid="view-list-btn">
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>

          {showFilters && (
            <CardContent className="border-t pt-4">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search by address, city, or state..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs">Deal Type</Label>
                  <Select value={dealTypeFilter} onValueChange={setDealTypeFilter}>
                    <SelectTrigger data-testid="filter-deal-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{DEAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Property Type</Label>
                  <Select value={propertyTypeFilter} onValueChange={setPropertyTypeFilter}>
                    <SelectTrigger data-testid="filter-property-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Location (State)</Label>
                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger data-testid="filter-state"><SelectValue /></SelectTrigger>
                    <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>{SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Price Range</Label>
                    <span className="text-xs text-muted-foreground">{formatCurrency(priceRange[0])} - {formatCurrency(priceRange[1])}</span>
                  </div>
                  <Slider value={priceRange} onValueChange={setPriceRange} min={0} max={10000000} step={10000} data-testid="filter-price-range" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">ARV Range</Label>
                    <span className="text-xs text-muted-foreground">{formatCurrency(arvRange[0])} - {formatCurrency(arvRange[1])}</span>
                  </div>
                  <Slider value={arvRange} onValueChange={setArvRange} min={0} max={20000000} step={25000} data-testid="filter-arv-range" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Profit Range</Label>
                    <span className="text-xs text-muted-foreground">{formatCurrency(profitRange[0])} - {formatCurrency(profitRange[1])}</span>
                  </div>
                  <Slider value={profitRange} onValueChange={setProfitRange} min={-1000000} max={5000000} step={5000} data-testid="filter-profit-range" />
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-2" />
                    Clear All Filters
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">
              {Math.min((currentPage - 1) * DEALS_PER_PAGE + 1, filteredDeals.length)}-{Math.min(currentPage * DEALS_PER_PAGE, filteredDeals.length)}
            </span> of <span className="font-medium text-foreground">{filteredDeals.length}</span> deals
            {hasActiveFilters && " (filtered)"}
          </p>
        </div>

        {/* Deal Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : filteredDeals.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No deals found</h3>
                <p className="text-muted-foreground mb-4">
                  {hasActiveFilters ? "Try adjusting your filters to see more results." : "Check back soon for new investment opportunities."}
                </p>
                {hasActiveFilters && <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
              {paginatedDeals.map((deal) => (
                <DealCard key={deal.deal_id} deal={deal} viewMode={viewMode} onViewDeal={() => setSelectedDeal(deal)} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="pagination-controls">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="pagination-prev">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <Button key={page} variant={page === currentPage ? "default" : "outline"} size="sm" className="w-9 h-9" onClick={() => setCurrentPage(page)} data-testid={`pagination-page-${page}`}>
                      {page}
                    </Button>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-testid="pagination-next">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <DealDetailModal
        deal={selectedDeal}
        open={!!selectedDeal}
        onOpenChange={(open) => !open && setSelectedDeal(null)}
        onDealUpdate={fetchDeals}
      />
    </DashboardLayout>
  );
};

export default InvestorLiveDealsPage;
